import { styled } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

import { ReactComponent as SearchIcon } from '@/assets/icons/search.icon.svg';
import { ReactComponent as ArrowIcon } from '@/assets/icons/arrow-tr.icon.svg';
import { ReactComponent as FilterIcon } from '@/assets/icons/filter.icon.svg';
import { ReactComponent as ArrowUpIcon } from '@/assets/icons/arrow-up.icon.svg';
import { ReactComponent as ArrowDownIcon } from '@/assets/icons/arrow-down.icon.svg';
import { ReactComponent as UncheckIcon } from '@/assets/icons/uncheck.icon.svg';
import { ReactComponent as CheckIcon } from '@/assets/icons/check.icon.svg';
import { ReactComponent as MoreIcon } from '@/assets/icons/more.icon.svg';
import { ReactComponent as CloseIcon } from '@/assets/icons/cancel.icon.svg';
import { ReactComponent as NextIcon } from '@/assets/icons/nav-right.icon.svg';
import { ReactComponent as PrevIcon } from '@/assets/icons/nav-left.icon.svg';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSortableData } from '@/hooks/useSortableData';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import useOnClickOutside from '@/hooks/useClickOutside';
import { exportUser, fetchUsers, generateStatement } from '@/api/userApi';
import TableLoader from '@/loaders/TableLoader';
import useDebounce from '@/hooks/useDebounce';
import { Oval } from 'react-loader-spinner';
import { fetchSubscriptions } from '@/api/subscriptionApi';
import SubscribeModal from './SubscribeModal';
import toast from 'react-hot-toast';
import DeactivateModal from './DeactivateModal';
import ActivateModal from './ActivateModal';
import { usePermissions } from '@/hooks/usePermissions';
import TableRowsSelect from '@/components/TableRowSelect';
import { formatInTimeZone } from 'date-fns-tz';
import { useDispatch } from 'react-redux';
import AddLenderModal from './AddLenderModal';
import { setSelected } from '@/redux/features/appSlice';
import LockWalletModal from './LockWalletModal';
// import UserTransferLimitModal from './UserTransferLimitModal';
import Button from '@/components/Button';
// import { SetUsersBulkLimitModal } from './SetUsersBulkLimitModal';
import { SearchControl } from '@/components/SearchControl';
import PromptModal from '@/components/PromptModal';
import { UserPeriodicModal } from './UserPeriodicModal';
import { formatSearchText } from '@/utils/helpers';

function UsersTable() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchExact, setSearchExact] = useState(false);
  const [searchFilters, setSearchFilters] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [showMenu, setShowMenu] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const limit = parseInt(searchParams.get('limit')) || 10;
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [fetch, setFetch] = useState('');
  const [fetchValue, setFetchValue] = useState('');
  const [orderBy, setOrderBy] = useState('');
  const [orderByValue, setOrderByValue] = useState('');
  const PAGE_LIMIT = 10;
  const debouncedsearch = useDebounce(search, 500);
  const selectedUsersParam = searchParams.get('selected') || '';
  const [selectedUsers, setSelectedUsers] = useState(() =>
    selectedUsersParam ? selectedUsersParam.split(',') : []
  );
  const [selectedUser, setSelectedUser] = useState(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [showLockModal, setShowLockModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showAddAsLenderModal, setShowAddAsLenderModal] = useState(false);
  const [showTransferLimitModal, setShowTransferLimitModal] = useState(false);
  const [data, setData] = useState({
    startDate: null,
    endDate: null
  });

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const currentPage = params.get('page');
  const [page, setPage] = useState(currentPage ? parseInt(currentPage) : 1);

  const ref = useRef();
  useOnClickOutside(ref, () => setShowMenu(false));

  const filterRef = useRef();
  useOnClickOutside(filterRef, () => setShowFilterMenu(false));

  const exportRef = useRef();
  useOnClickOutside(exportRef, () => setShowExportMenu(false));

  const searchInputRef = useRef(null);

  const { hasPermission, superAdmin } = usePermissions();
  const isASuperAdmin = superAdmin(true);
  const canUpdateUsers = hasPermission('users', 'update');

  const filterOptions = [
    { value: 'currentWeek', label: 'Current Week' },
    { value: 'lastWeek', label: 'Last Week' },
    { value: 'currentMonth', label: 'Current Month' },
    { value: 'lastMonth', label: 'Last Month' },
    { value: 'currentYear', label: 'Current Year' },
    { value: 'lastYear', label: 'Last Year' }
  ];

  const searchOptions = [
    { value: 'email', label: 'Email' },
    { value: 'firstName', label: 'First Name' },
    { value: 'companyName', label: 'Company Name' },
    { value: 'lastName', label: 'Last Name' },
    { value: 'phoneNumber', label: 'Phone Number' }
  ];


  const users = useQuery({
    queryKey: [
      'users',
      {
        page,
        limit,
        search: formatSearchText(debouncedsearch, searchExact, searchFilters),
        orderBy,
        fetch,
        searchBy: searchFilters,
        searchByExactMatch: searchExact
      }
    ],
    queryFn: fetchUsers,
    keepPreviousData: true
  });

  const subscriptions = useQuery({
    queryKey: ['subscriptions'],
    queryFn: fetchSubscriptions,
    keepPreviousData: true
  });

  const statement = useQuery({
    queryKey: ['statement'],
    queryFn: generateStatement,
    enabled: false
  });

  // const handleGenerateStatement = () => {
  //   if (statement.data.statusCode === 200) {
  //     setShowGenerateModal(false);
  //     toast.success("Statement has been sent to users emails");
  //   }
  // }

   const handleGenerateStatement = async () => {
     try {
       // Manually trigger the query
       const result = await statement.refetch();

       if (result.data.statusCode === 200) {
         setShowGenerateModal(false);
         toast.success('Statement has been sent to users emails');
       }
     } catch (error) {
       // Handle error if needed
       console.error('Error generating statement:', error);
     }
   };

  //Exports
  const exportUsers = useMutation({
    mutationFn: (data) => exportUser(data),
    onSuccess: (data) => {
      setData({
        startDate: null,
        endDate: null
      });
      setShowExportMenu(false);
      toast.success(data.message);
    }
  });

  const handleData = (e) => {
    const { name, value } = e.target;
    const date = new Date(value);
    const formattedDate = date.toISOString().split('T')[0];

    setData((prevData) => ({
      ...prevData,
      [name]: formattedDate
    }));
  };

  const handleExport = () => {
    exportUsers.mutate(data);
  };

  const handleSearchFilter = (newFilters) => {
    setSearchExact(exactMatch);
    setSearchFilters(newFilters);
  };

  useEffect(() => {
    const params = new URLSearchParams();
    if (page > 1) params.set('page', page);
    if (limit !== 10) params.set('limit', limit);
    if (debouncedsearch) params.set('search', debouncedsearch);

    navigate(`?${params.toString()}`, { replace: true });
  }, [page, limit, debouncedsearch, navigate]);

  const handleSearchClear = () => {
    setSearch('');
    setPage(1);
    const params = new URLSearchParams(searchParams);
    params.delete('search');
    setSearchParams(params);
  };

  useEffect(() => {
    const input = searchInputRef.current;
    if (!input) return;

    const handleNativeClear = () => handleSearchClear();
    input.addEventListener('search', handleNativeClear);
    return () => input.removeEventListener('search', handleNativeClear);
  }, [searchParams]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (selectedUsers.length > 0) {
      params.set('selected', selectedUsers.join(','));
    } else {
      params.delete('selected');
    }
    setSearchParams(params, { replace: true });
  }, [selectedUsers]);

  const { items, requestSort, sortConfig } = useSortableData(users?.data?.data?.users || []);
  const getClassNamesFor = (name) => {
    if (!sortConfig) {
      return;
    }
    return sortConfig.key === name ? sortConfig.direction : undefined;
  };

  const handleRowsPerPageChange = (value) => {
    setSearchParams({ page, limit: value });
    console.log(value);
  };

  const fetchNextPage = () => {
    const totalPage = Math.ceil(users?.data?.data?.meta?.totalItems / limit);
    if (page < totalPage) {
      const nextPage = page + 1;
      const params = new URLSearchParams(searchParams);
      params.set('page', String(nextPage));
      params.set('limit', String(limit));
      if (selectedUsers.length > 0) {
        params.set('selected', selectedUsers.join(','));
      }
      setPage(nextPage);
      navigate(`/users?${params.toString()}`);
    }
  };

  const fetchPrevPage = () => {
    if (page > 1) {
      const prevPage = page - 1;
      const params = new URLSearchParams(searchParams);
      params.set('page', String(prevPage));
      params.set('limit', String(limit));
      if (selectedUsers.length > 0) {
        params.set('selected', selectedUsers.join(','));
      }
      setPage(prevPage);
      navigate(`/users?${params.toString()}`);
    }
  };

  const handleSelectItems = (id) => {
    if (selectedUsers.includes(id)) {
      const filtered = selectedUsers.filter((item) => item !== id);
      setSelectedUsers(filtered);
    } else {
      setSelectedUsers((prev) => [...prev, id]);
    }
  };

  // const handleSelectAllItems = () => {
  //   if (selectedUsers.length === items.length) {
  //     setSelectedUsers([]);
  //   } else {
  //     setSelectedUsers(items.map((item) => item.id));
  //   }
  // };

  const handleSelectAllItems = () => {
    const currentPageItems = items.map((item) => item.id);

    if (currentPageItems.every((id) => selectedUsers.includes(id))) {
      // If all items on current page are selected, deselect them
      setSelectedUsers((prev) => prev.filter((id) => !currentPageItems.includes(id)));
    } else {
      // If not all items are selected, add the missing ones
      setSelectedUsers((prev) => [...new Set([...prev, ...currentPageItems])]);
    }
  };

  const handleUpdateDeactivate = (id) => {
    const isAllowed = canUpdateUsers || isASuperAdmin;
    if (!isAllowed) {
      toast.error('You do not have permission to update');
      return;
    } else {
      setSelectedUser(id);
      setShowDeactivateModal(true);
    }
  };

  const handleModal = () => {
    const isAllowed = canUpdateUsers || isASuperAdmin;
    if (!isAllowed) {
      toast.error('You do not have permission to update');
      return;
    } else {
      setShowGenerateModal(true);
    }
  };

  const handleUpdateSubscription = (id) => {
    const isAllowed = canUpdateUsers || isASuperAdmin;
    if (!isAllowed) {
      toast.error('You do not have permission to update');
      return;
    } else {
      setShowSubscriptionModal(true);
      setSelectedUsers([id]);
      console.log('Not allowed');
    }
  };

  const handleButton = () => {
    const isAllowed = canUpdateUsers || isASuperAdmin;
    if (!isAllowed) {
      toast.error('You do not have permission to update');
      return;
    } else {
      setShowSubscriptionModal(true);
    }
  };

  const handleUpdateActivate = (id) => {
    const isAllowed = canUpdateUsers || isASuperAdmin;
    if (!isAllowed) {
      toast.error('You do not have permission to update');
      return;
    } else {
      setSelectedUser(id);
      setShowActivateModal(true);
      console.log('update');
    }
  };

  const handleUpdateLender = (id) => {
    const isAllowed = canUpdateUsers || isASuperAdmin;
    if (!isAllowed) {
      toast.error('You do not have permission to update');
      return;
    } else {
      setSelectedUser(id);
      setShowAddAsLenderModal(true);
    }
  };

  const handleLockWallet = (id) => {
    const isAllowed = canUpdateUsers || isASuperAdmin;
    if (!isAllowed) {
      toast.error('You do not have permission to update');
      return;
    } else {
      setSelectedUser(id);
      setShowLockModal(true);
    }
  };

  // const handleTransferLimit = (id) => {
  //   const isAllowed = canUpdateUsers || isASuperAdmin;
  //   if (!isAllowed) {
  //     toast.error('You do not have permission to update');
  //     return;
  //   } else {
  //     setSelectedUser(id);
  //     setShowTransferLimitModal(true);
  //   }
  // };

  const handleWalletNavigation = (item, id) => {
    if (item === null) {
      toast.error('User does not have a wallet');
      return;
    } else {
      navigate(`/wallets/${id}`);
    }
  };

  // const handleVirtualAccountNavigation = (item, id) => {
  //   if (!item || item.length === 0) {
  //     toast.error('User does not have a virtual account');
  //     return;
  //   } else {
  //     navigate(`/users/accounts/${id}?page=${page}`);
  //   }
  //   if (!id) {
  //     toast.error('User does not have a virtual account');
  //     return;
  //   } else {
  //     navigate(`/users/accounts/${id}?page=${page}`);
  //   }
  // };

  const handleVirtualAccountNavigation = (id) => {
    // if (!item || item.length === 0) {
    //   toast.error('User does not have a virtual account');
    //   return;
    // } else {
    //   navigate(`/users/accounts/${id}?page=${page}`);
    // }
    if (!id) {
      toast.error('User does not have a virtual account');
      return;
    } else {
      navigate(`/users/accounts/${id}?page=${page}`);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    if (value.trim()) setPage(1);
  };

  const displayButton = canUpdateUsers || isASuperAdmin;

  if ((users.isFetching && !search) || (subscriptions.isFetching && !search)) {
    return <TableLoader />;
  }

  return (
    <UsersTableView users={users} page={page} setPage={setPage}>
      {displayButton && (<div className="end">
        <Button
          width="20%"
          height="48px"
          type="button"
          style={{ padding: '0 20px' }}
          onClick={() => {
            handleModal();
          }}>
          Generate Statement
        </Button>
      </div>)}
      <TableHeader>
        <SearchView>
          <SearchIcon />
          <input
            type="search"
            value={search}
            ref={searchInputRef}
            placeholder="Search users"
            onChange={handleSearchChange}
          />
          {users.isLoading && search && <Oval width={20} height={20} strokeWidth={4} />}
          <SearchControl
            onSearch={handleSearchFilter}
            currentFilters={searchFilters}
            availableFilters={searchOptions}
            searchExact={searchExact}
            onSearchExactChange={setSearchExact}
          />
        </SearchView>
        <FilterView>
          {selectedUsers.length > 0 && canUpdateUsers && (
            <button className="sub-update" type="button" onClick={() => handleButton()}>
              Update subscription
            </button>
          )}
          <button className="export" onClick={() => setShowExportMenu(true)}>
            <ArrowIcon /> Export
          </button>
          {/* ===== Export Menu ====== */}
          <AnimatePresence>
            {showExportMenu ? (
              <motion.div
                className="export-menu"
                ref={exportRef}
                initial={{ y: -20 }}
                animate={{ y: 0 }}
                exit={{ y: -10, opacity: 0 }}
                transition={{ duration: 0.2 }}>
                <div className="heading">
                  <span>Export</span>
                </div>
                <div className="body">
                  <div className="item">
                    <div className="date-input">
                      <label>From:</label>
                      <input
                        type="date"
                        name="startDate"
                        value={data.startDate}
                        onChange={handleData}
                      />
                    </div>
                    <div className="date-input">
                      <label>To:</label>
                      <input
                        type="date"
                        name="endDate"
                        value={data.endDate}
                        onChange={handleData}
                      />
                    </div>
                  </div>
                  <button className="done" onClick={handleExport}>
                    {exportUsers?.isLoading ? (
                      <Oval height={20} width={20} color="#c2bdbd" secondaryColor="#ddd" />
                    ) : (
                      'Export'
                    )}
                  </button>
                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>

          <button className="filter" onClick={() => setShowFilterMenu(true)}>
            <FilterIcon /> Filter
          </button>
          {/* ===== Filter Menu ====== */}
          <AnimatePresence>
            {showFilterMenu ? (
              <motion.div
                className="filter-menu"
                ref={filterRef}
                initial={{ y: -20 }}
                animate={{ y: 0 }}
                exit={{ y: -10, opacity: 0 }}
                transition={{ duration: 0.2 }}>
                <div className="heading">
                  <span>Filters</span>
                  <button
                    className="done"
                    onClick={() => {
                      setFetch(fetchValue);
                      setOrderBy(orderByValue);
                      setShowFilterMenu(false);
                    }}>
                    Done
                  </button>
                </div>
                <div className="body">
                  <div className="item">
                    <h3>Duration</h3>
                    {filterOptions.map((option) => {
                      return (
                        <div className="input" key={option.value}>
                          <input
                            type="radio"
                            name="duration"
                            value={option.value}
                            onChange={(e) => {
                              setFetchValue(e.target.value);
                            }}
                          />
                          <label>{option.label}</label>
                        </div>
                      );
                    })}
                  </div>
                  <div className="item">
                    <h3>Order</h3>
                    <select
                      name="plan"
                      id="plan"
                      onChange={(e) => {
                        setOrderByValue(e.target.value);
                      }}>
                      <option value="">Select order</option>
                      <option value="ASC">Ascending</option>
                      <option value="DESC">Descending</option>
                    </select>
                  </div>
                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </FilterView>
      </TableHeader>
      <TableHead>
        <div className="check-icon">
          <button type="button" onClick={handleSelectAllItems}>
            {/* {selectedUsers?.length === items?.length ? <CheckIcon /> : <UncheckIcon />} */}
            {items.every((item) => selectedUsers.includes(item.id)) ? (
              <CheckIcon />
            ) : items.some((item) => selectedUsers.includes(item.id)) ? (
              <CheckIcon /> // You'll need to create or import this component
            ) : (
              <UncheckIcon />
            )}
          </button>
        </div>
        <div
          className={`name ${getClassNamesFor('firstName')}`}
          onClick={() => requestSort('firstName')}>
          <span>Name</span>
          <span className="icon">
            <ArrowUpIcon className="up" />
            <ArrowDownIcon className="down" />
          </span>
        </div>
        <div
          className={`name ${getClassNamesFor('companies')}`}
          onClick={() => requestSort('companies')}>
          <span>Company</span>
          <span className="icon">
            <ArrowUpIcon className="up" />
            <ArrowDownIcon className="down" />
          </span>
        </div>
        <div className={`email ${getClassNamesFor('email')}`} onClick={() => requestSort('email')}>
          <span>Email</span>
          <span className="icon">
            <ArrowUpIcon className="up" />
            <ArrowDownIcon className="down" />
          </span>
        </div>
        <div
          className={`referral ${getClassNamesFor('signupReferringPlatform')}`}
          onClick={() => requestSort('signupReferringPlatform')}>
          <span>Referred from</span>
          <span className="icon">
            <ArrowUpIcon className="up" />
            <ArrowDownIcon className="down" />
          </span>
        </div>
        <div
          className={`phone ${getClassNamesFor('phoneNumber')}`}
          onClick={() => requestSort('phoneNumber')}>
          <span>Phone</span>
          <span className="icon">
            <ArrowUpIcon className="up" />
            <ArrowDownIcon className="down" />
          </span>
        </div>
        <div
          className={`date ${getClassNamesFor('createdAt')}`}
          onClick={() => requestSort('createdAt')}>
          <span>Date joined</span>
          <span className="icon">
            <ArrowUpIcon className="up" />
            <ArrowDownIcon className="down" />
          </span>
        </div>
        <div className="actions"></div>
      </TableHead>

      <TableBody>
        {items.map((item, i) => {
          return (
            <div key={item?.id} className="item">
              <div className="check-icon">
                <button
                  type="button"
                  onClick={() => {
                    handleSelectItems(item?.id);
                  }}
                  className="checkbox">
                  {selectedUsers.includes(item?.id) ? <CheckIcon /> : <UncheckIcon />}
                </button>
              </div>
              <div className="name">
                <span>
                  {item.firstName || '-'} {item.lastName || '-'}
                </span>
              </div>
              <div className="name">
                {item?.companies?.length > 0 ? (
                  <span>{item?.companies[0]?.name || '-'}</span>
                ) : (
                  <span>-</span>
                )}
              </div>
              <div className="email">
                <span>{item.email || '-'}</span>
              </div>
              <div className="referral">
                <span>{item.signupReferringPlatform || '-'}</span>
              </div>
              <div className="phone">
                <span>{item.phoneNumber || '-'}</span>
              </div>
              <div className="date">
                <span>{formatInTimeZone(new Date(item?.createdAt), 'UTC', 'do MMM, yyyy')}</span>
                <p>{formatInTimeZone(new Date(item?.createdAt), 'UTC', 'h:mm aa')}</p>
              </div>
              <div className="actions">
                <button
                  className="menu-icon"
                  onClick={() => {
                    setShowMenu(true);
                    setCurrentIndex(i);
                  }}>
                  <MoreIcon />
                </button>

                {/* ===== Dropdown Menu ====== */}
                <AnimatePresence>
                  {showMenu && currentIndex === i ? (
                    <motion.div
                      className="menu-actions"
                      ref={ref}
                      initial={{ y: -20 }}
                      animate={{ y: 0 }}
                      exit={{ y: -10, opacity: 0 }}
                      transition={{ duration: 0.2 }}>
                      <button
                        onClick={() => {
                          navigate(`/users/${item?.id}?page=${page}`, dispatch(setSelected(item)));
                        }}>
                        <span>View user</span>
                      </button>
                      <button
                        onClick={() => {
                          handleWalletNavigation(item?.wallet, item?.wallet?.id);
                        }}>
                        <span>View user wallet</span>
                      </button>
                      <button
                        onClick={() => {
                          handleLockWallet(item?.id);
                        }}>
                        <span>Lock user wallet</span>
                      </button>
                      <button
                        onClick={() => {
                          handleVirtualAccountNavigation(item?.company);
                        }}>
                        <span>View virtual account</span>
                      </button>
                      <button
                        onClick={() => {
                          handleUpdateSubscription(item?.id);
                        }}>
                        <span>Update subscription</span>
                      </button>
                      {item?.isDeactivated === false && (
                        <button
                          onClick={() => {
                            handleUpdateDeactivate(item?.id);
                          }}>
                          <span>Deactivate user</span>
                        </button>
                      )}
                      {item?.isDeactivated === true && (
                        <button
                          onClick={() => {
                            handleUpdateActivate(item?.id);
                          }}>
                          <span>Activate user</span>
                        </button>
                      )}
                      {item?.isLender === false && (
                        <button
                          onClick={() => {
                            handleUpdateLender(item?.id);
                          }}>
                          <span>Add as Lender</span>
                        </button>
                      )}
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </div>
            </div>
          );
        })}
      </TableBody>

      {/* ===== Pagination ====== */}
      {users?.data?.data?.meta?.totalItems > 0 && (
        <PaginationView>
          <TableRowsSelect onChange={handleRowsPerPageChange} />
          <div className="paginate">
            <button className="prev" onClick={fetchPrevPage}>
              <PrevIcon />
            </button>
            <span>
              Page {page} of {Math.ceil(users?.data?.data?.meta?.totalItems / limit)}
            </span>
            <button className="next" onClick={fetchNextPage}>
              <NextIcon />
            </button>
          </div>
        </PaginationView>
      )}

      <SubscribeModal
        showModal={showSubscriptionModal}
        setShowModal={setShowSubscriptionModal}
        subscriptions={subscriptions?.data?.data}
        userId={selectedUsers}
        setSelectedUsers={setSelectedUsers}
      />

      <DeactivateModal
        showModal={showDeactivateModal}
        setShowModal={setShowDeactivateModal}
        selectedUser={selectedUser}
        setSelectedUser={setSelectedUser}
      />

      <ActivateModal
        showModal={showActivateModal}
        setShowModal={setShowActivateModal}
        selectedUser={selectedUser}
        setSelectedUser={setSelectedUser}
      />

      <AddLenderModal
        showModal={showAddAsLenderModal}
        setShowModal={setShowAddAsLenderModal}
        selectedUser={selectedUser}
      />

      <LockWalletModal
        item={selectedUser}
        showModal={showLockModal}
        setShowModal={setShowLockModal}
      />

      <UserPeriodicModal showModal={showGenerateModal} setShowModal={setShowGenerateModal} />

      {/* <SetUsersBulkLimitModal
        selectedUsers={selectedUsers}
        showModal={showBulkLimitModal}
        setSelectedUsers={setSelectedUsers}
        setShowModal={setShowBukLimitModal}
      /> */}
    </UsersTableView>
  );
}

export default UsersTable;

const UsersTableView = styled.div`
  width: 100%;
  height: 100%;
  margin-top: 50px;

  .end {
    display: flex;
    margin-bottom: 5px;
    justify-content: end;
  }
`;

const TableHeader = styled.div`
  width: 100%;
  padding: 20px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.colors.line};
  border-left: none;
  border-right: none;
`;

const SearchView = styled.div`
  width: 40%;
  height: 40px;
  display: flex;
  align-items: center;

  input {
    width: 100%;
    height: 100%;
    outline: none;
    border: none;
    margin-left: 10px;
    font-weight: 400;

    &::placeholder {
      font-size: 1rem;
      font-weight: 400;
      color: ${({ theme }) => theme.colors.placeholder};
    }
  }
`;

const FilterView = styled.div`
  width: 40%;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  position: relative;

  .filter-menu {
    position: absolute;
    top: 35px;
    right: 0px;
    background-color: ${({ theme }) => theme.colors.white};
    box-shadow:
      0px 0px 2px rgba(125, 125, 125, 0.1),
      0px 2px 10px rgba(0, 0, 0, 0.1);
    border-radius: 10px;
    z-index: 10;
    width: 300px;
    border: none;
    padding: 20px 12px;

    .heading {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 20px;

      span {
        font-size: 1.1rem;
        font-weight: 500;
        color: ${({ theme }) => theme.colors?.black};
      }

      .done {
        background-color: transparent;
        color: ${({ theme }) => theme.colors?.secondary};
        border: none;
        outline: none;
        font-size: 0.875rem;
        font-weight: 600;
        cursor: pointer;
        margin: 0;
        padding: 0;
        width: auto;
      }
    }

    .body {
      .item {
        width: 100%;
        margin-bottom: 20px;

        h3 {
          font-size: 1rem;
          font-weight: 500;
          color: ${({ theme }) => theme.colors?.black};
          margin-bottom: 10px;
        }

        .input {
          display: flex;
          align-items: center;
          margin-bottom: 10px;

          input {
            margin-right: 10px;
            accent-color: ${({ theme }) => theme.colors?.secondary};
            cursor: pointer;
          }
        }

        select {
          width: 100%;
          height: 40px;
          outline: none;
          border: 1px solid ${({ theme }) => theme.colors.line};
          border-radius: 6px;
          padding: 0 10px;
          font-size: 0.875rem;
          font-weight: 400;
          color: ${({ theme }) => theme.colors?.placeholder};
          cursor: pointer;
        }
      }
    }
  }

  .export-menu {
    position: absolute;
    top: 35px;
    right: 0px;
    background-color: ${({ theme }) => theme.colors.white};
    box-shadow:
      0px 0px 2px rgba(125, 125, 125, 0.1),
      0px 2px 10px rgba(0, 0, 0, 0.1);
    border-radius: 10px;
    z-index: 10;
    width: 300px;
    border: none;
    padding: 20px 12px;

    .body {
      margin-top: 20px;

      .item {
        width: 100%;
        margin-bottom: 20px;

        .date-input {
          gap: 0.4rem;
          display: flex;
          align-items: start;
          margin-bottom: 10px;
          flex-direction: column;
          justify-content: start;

          input {
            width: 100%;
            border: none;
            cursor: pointer;
            margin-right: 10px;
            border-radius: 6px;
            padding: 0.5rem 0.65rem;
            color: ${({ theme }) => theme.colors?.black};
            background-color: ${({ theme }) => theme.colors?.inputBackground};
          }
        }
      }

      .done {
        margin: 0;
        width: auto;
        border: none;
        outline: none;
        cursor: pointer;
        font-weight: 600;
        border-radius: 6px;
        padding: 0.6rem 2rem;
        font-size: 0.875rem;
        color: ${({ theme }) => theme.colors?.white};
        background-color: ${({ theme }) => theme.colors?.secondary};
      }
    }
  }

  button {
    width: 120px;
    height: 100%;
    border: none;
    outline: none;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      margin-right: 10px;
    }
  }

  .sub-update {
    width: 200px;
    background-color: ${({ theme }) => theme.colors?.secondary};
    border-radius: 6px;
    color: ${({ theme }) => theme.colors?.white};
    font-weight: 600;
    transition: all 0.2s ease-in-out;
  }
`;

const TableHead = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.line};

  div {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    cursor: pointer;
    height: 100%;

    span {
      font-size: 0.8rem;
      font-weight: 500;
      color: ${({ theme }) => theme.colors?.black};
    }

    .icon {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      margin-left: 4px;
      width: 10px;
    }
  }

  .check-icon {
    flex: 0.15;
  }

  .ascending {
    color: ${({ theme }) => theme.colors.black};

    .up path {
      stroke: ${({ theme }) => theme.colors.black};
      color: ${({ theme }) => theme.colors.black};
    }
  }

  .descending {
    color: ${({ theme }) => theme.colors.black};

    .down path {
      stroke: ${({ theme }) => theme.colors.black};
      color: ${({ theme }) => theme.colors.black};
    }
  }

  .name {
    flex: 1;
  }

  .email {
    flex: 1;
  }

  .referral {
    flex: 0.6;
  }

  .status {
    flex: 0.5;
  }

  .phone {
    flex: 0.7;
  }

  .date {
    flex: 0.5;
  }

  .actions {
    flex: 0.3;
  }
`;

const TableBody = styled.div`
  width: 100%;

  .item {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 20px;
    border-bottom: 1px solid ${({ theme }) => theme.colors?.line};

    div {
      flex: 1;
      flex-wrap: wrap;

      span {
        font-size: 0.75rem;
        font-weight: 500;
        color: ${({ theme }) => theme.colors?.secondary};
        text-transform: capitalize;
        word-break: break-all;
      }

      p {
        font-size: 0.75rem;
        font-weight: 500;
        color: ${({ theme }) => theme.colors?.secondary};
        text-transform: capitalize;
      }
    }

    .check-icon {
      flex: 0.15;
      display: flex;
      align-items: center;
    }

    .name {
      flex: 1;
    }

    .email {
      flex: 1;
      flex-wrap: wrap;

      span {
        text-transform: none;
      }
    }

    .referral {
      flex: 0.6;
    }

    .status {
      flex: 0.5;
    }

    .phone {
      flex: 0.7;
    }

    .date {
      flex: 0.5;
    }

    .actions {
      flex: 0.3;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      position: relative;

      svg {
        cursor: pointer;

        path {
          stroke: ${({ theme }) => theme.colors?.secondary};
        }
      }

      .menu-actions {
        position: absolute;
        top: 22px;
        right: 0px;
        background-color: ${({ theme }) => theme.colors.white};
        box-shadow:
          0px 0px 2px rgba(125, 125, 125, 0.1),
          0px 2px 10px rgba(0, 0, 0, 0.1);
        border-radius: 10px;
        z-index: 10;
        width: 200px;
        border: none;

        button {
          width: 100%;
          padding: 16px 12px;
          font-size: 0.875rem;
          background-color: transparent;
          transition: all 0.2s ease-in-out;
          border: none;
          outline: none;
          display: flex;
          justify-content: flex-start;
          align-items: center;

          &:hover {
            background-color: ${({ theme }) => theme.colors.layer3};
          }

          span {
            color: ${({ theme }) => theme.colors.activeTitle};
            width: calc(100% - 35px);
            display: flex;
            justify-content: flex-start;
            text-align: left;
            font-weight: 400;
          }
        }
      }
    }
  }
`;

const PaginationView = styled.div`
  width: 100%;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .total {
    span {
      font-size: 0.875rem;
      font-weight: 500;
      color: ${({ theme }) => theme.colors?.secondary};
    }
  }

  .paginate {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: 20px;
    button {
      width: 40px;
      height: 40px;
      border: none;
      outline: none;
      background-color: ${({ theme }) => theme.colors?.layer3};
      color: ${({ theme }) => theme.colors?.secondary};
      font-size: 0.875rem;
      font-weight: 500;
      border-radius: 6px;

      &:hover {
        background-color: ${({ theme }) => theme.colors?.layer2};
      }
    }

    span {
      margin: 0 10px;
      font-size: 0.875rem;
      font-weight: 500;
      color: ${({ theme }) => theme.colors?.secondary};
    }
  }
`;
